import type { State } from "../../State";
import type { TechnicalReportId } from "./catalog";

/** Preserves the existing general and document-specific notes as plain text. */
export function technicalReportNotes(app: State, id?: TechnicalReportId): string[] {
  const itx = app.translate.bind(app);
  const priceListWarning =
    id === "productPrices"
      ? {
          text: itx(
            "pdfExport.sharedRetailerPricesNotice",
            "Preise und Verfügbarkeit stammen aus denselben gespeicherten Händlerdaten wie in der Produktansicht. Fehlende Daten werden beim Export nachgeladen.",
          ),
        }
      : undefined;
  const warnings: { text: string; style?: Record<string, string> }[] = [
    {
      text: itx("warningPdf.header", "Allgemeine Hinweise"),
      style: { fontSize: "12px", fontWeight: "bold", padding: "5px" },
    },
    ...(priceListWarning ? [priceListWarning] : []),
    {
      text: itx(
        "warningPdf.nonBindingProposal",
        "Bei der vorliegenden Planung handelt es sich um einen unverbindlichen Vorschlag, der auf den Angaben des Kunden basiert und nicht von einer Elektrofachkraft erstellt wurde. Dies bedarf vor Umsetzung, durch eine Elektrofachkraft, der Prüfung und Abnahme durch eine solche.",
      ),
    },
    {
      text: itx(
        "warningPdf.electricalWorkWarning",
        "Achtung: Elektrotechnische Arbeiten dürfen nur durch Elektrofachkräfte oder unter deren Leitung und Aufsicht ausgeführt werden!",
      ),
    },
  ];
  // if project has any dali consumer or device add dali warning
  if (app.allConsumers().some((consumer) => consumer.isDali) || app.allDevices().some((device) => device.isDali)) {
    warnings.push({
      text: itx("warningPdf.daliWarningHeader", "Hinweise zu DALI Verbrauchern und Geräten"),
      style: { fontSize: "12px", fontWeight: "bold", padding: "5px" },
    });
    warnings.push({
      text: itx(
        "warningPdf.daliWarning",
        "In diesem Projekt sind DALI Verbraucher und/oder Geräte vorhanden. Bitte beachte die speziellen Anforderungen an die Verkabelung und Installation von DALI Systemen, um eine ordnungsgemäße Funktion und Sicherheit zu gewährleisten.",
      ),
    });
  }

  if (id === "cablePull") {
    warnings.push(
      ...[
        {
          text: itx("cablePullList.definitionsTitle", "Hinweise zur KNX Leitung"),
          style: { fontSize: "12px", fontWeight: "bold", padding: "5px" },
        },
        {
          text:
            "• " + // no itx
            itx(
              "cablePullList.knxInStarBusTree",
              "Die KNX Leitung kann im Stern, als Bus, Linie oder Baum verlegt werden. Es wird empfohlen die KNX Leitung vom Schaltschrank durch jede Etage von Taster- und Sensorstelle zu Taster- und Sensorstelle zu verlegen und wieder im Schaltschrank zu Enden (Achtung: der Ring darf am Ende im Verteilerschranks nicht geschlossen werden, offener Ring, da es nur einen Verbindungsweg geben darf!).  Ebenso sollte eine separate KNX Leitung für den Außenbereich genutzt werden. Hierdurch kann jede Etage und der Außenbereich nachträglich als separate Linie ausgeführt werden.",
            ),
        },
        {
          text:
            "• " + // no itx
            itx(
              "cablePullList.suspendedCeilingsRecommendation",
              "Bei abgehängten Decken, wird empfohlen in jedem Raum eine KNX Kabelschleife in der Decke für zusätzliche Sensoren vorzusehen, alternativ kann ein Deckenauslass pro Raum vorgesehen werden",
            ),
        },
        {
          text:
            "• " + // no itx
            itx(
              "cablePullList.maxFourCablesPerBox",
              "Mehr als vier Kabel in einer Dose sind nicht praktikabel, da die KNX Klemmen maximal jeweils 4 Adern aufnehmen können.",
            ),
        },
        {
          text:
            "• " + // no itx
            itx(
              "cablePullList.maxLineLength",
              "Eine Linie darf max. 1000m lang sein; max. 350m Abstand zwischen Netzteil und Teilnehmer",
            ),
        },
      ],
    );
  }

  return warnings.map(warning => warning.text);
}

/** Combines general notes and the selected documents' special notes without repetitions. */
export function projectGeneralNotes(app: State, chapterIds: readonly string[]): string[] {
  const notes = new Set(technicalReportNotes(app));
  for (const id of ["productPrices"] as const) {
    if (chapterIds.includes(id)) for (const note of technicalReportNotes(app, id)) notes.add(note);
  }
  notes.delete(app.translate("warningPdf.header", "Allgemeine Hinweise"));
  return [...notes];
}

/** Keeps chapter-specific explanations next to their list instead of the general page. */
export function technicalReportSpecificNotes(app: State, id: TechnicalReportId): string[] {
  const general = new Set(technicalReportNotes(app));
  return technicalReportNotes(app, id).filter(note => !general.has(note));
}
